import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi } from "../apiAdmin";

// Admin "Payment Guardrails" tab (Project Atlas) — the founder's launch
// checklist for global payouts. Item definitions live in server code
// (lib/payouts/guardrails.js); this tab renders them grouped by category and
// lets a superadmin move each through todo → building → built → verified /
// blocked, with running notes.
//
// "verified" is the payments expert's sign-off state — the whole tab exists to
// make "we built it" and "the expert confirmed it" visibly different things.

interface GuardrailItem {
  key: string;
  category: string;
  title: string;
  detail: string;
  status: string;
  /** Deployment fact from server code (not editable): is this protection active in prod? */
  live: "yes" | "partial" | "no";
  liveNote: string;
  notes: string;
  updatedBy: string;
  updatedAt: string | null;
}

interface GuardrailsData {
  summary: {
    total: number;
    verified: number;
    built: number;
    blocked: number;
    liveYes?: number;
    livePartial?: number;
    categories: { category: string; total: number; verified: number; built: number }[];
  };
  items: GuardrailItem[];
  statuses: string[];
}

const STATUS_STYLE: Record<string, { label: string; color: string; bg: string }> = {
  todo: { label: "TO DO", color: "var(--adm-text-sec)", bg: "rgba(139,148,158,.14)" },
  building: { label: "BUILDING", color: "var(--adm-blue)", bg: "rgba(88,166,255,.14)" },
  built: { label: "BUILT", color: "#b45309", bg: "rgba(255,209,102,.18)" },
  verified: { label: "✓ VERIFIED", color: "var(--adm-green)", bg: "rgba(36,201,97,.12)" },
  blocked: { label: "BLOCKED", color: "var(--adm-red)", bg: "rgba(248,81,73,.12)" },
};

const LIVE_STYLE: Record<GuardrailItem["live"], { label: string; color: string; bg: string }> = {
  yes: { label: "✅ LIVE", color: "var(--adm-green)", bg: "rgba(36,201,97,.12)" },
  partial: { label: "🟡 PARTLY", color: "#b45309", bg: "rgba(255,209,102,.18)" },
  no: { label: "—", color: "var(--adm-text-sec)", bg: "transparent" },
};

function LiveBadge({ live }: { live: GuardrailItem["live"] }) {
  const s = LIVE_STYLE[live] || LIVE_STYLE.no;
  return (
    <span
      style={{
        display: "inline-block",
        padding: live === "no" ? 0 : "3px 9px",
        borderRadius: 6,
        fontSize: "0.68rem",
        fontWeight: 700,
        whiteSpace: "nowrap",
        color: s.color,
        background: s.bg,
      }}
    >
      {s.label}
    </span>
  );
}

function StatusChip({ status }: { status: string }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.todo;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 9px",
        borderRadius: 6,
        fontSize: "0.68rem",
        fontWeight: 700,
        whiteSpace: "nowrap",
        color: s.color,
        background: s.bg,
      }}
    >
      {s.label}
    </span>
  );
}

const CATEGORY_ICONS: Record<string, string> = {
  "Platform hardening — live today": "🏰",
  "Identity & signup vetting": "🪪",
  "Fraud & AML": "🕵️",
  "Compliance & Legal": "⚖️",
  "MassPay gates": "🤝",
  "Stripe & rails": "🚄",
  "Infrastructure & ops": "🧱",
};

export default function AdminGuardrailsTab() {
  const [data, setData] = useState<GuardrailsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Per-item draft notes while editing (key → text); saved on blur/button.
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    adminApi
      .get("/guardrails")
      .then((r) => setData(r.data))
      .catch(() => setError("Couldn't load the guardrails board. Try refreshing."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const patch = useCallback(
    async (key: string, body: { status?: string; notes?: string }) => {
      setSavingKey(key);
      try {
        const { data: updated } = await adminApi.patch(`/guardrails/${key}`, body);
        setData((prev) =>
          prev
            ? {
                ...prev,
                items: prev.items.map((i) => (i.key === key ? { ...i, ...updated } : i)),
              }
            : prev
        );
        // Re-pull so the summary cards stay honest after a status change.
        if (body.status) load();
      } catch {
        setError("Update failed — are you signed in as a superadmin? (Only superadmins can edit.)");
      } finally {
        setSavingKey(null);
      }
    },
    [load]
  );

  const byCategory = useMemo(() => {
    const map: Record<string, GuardrailItem[]> = {};
    for (const item of data?.items || []) {
      (map[item.category] = map[item.category] || []).push(item);
    }
    return map;
  }, [data]);

  if (loading) return <p className="muted-text adm-p-12">Loading guardrails…</p>;
  if (error && !data) {
    return (
      <div className="adm-p-12">
        <p className="muted-text">{error}</p>
        <button className="adm-select adm-pointer" onClick={load}>Retry</button>
      </div>
    );
  }
  if (!data) return null;

  const { summary } = data;

  return (
    <div>
      <p className="muted-text adm-fs-82" style={{ maxWidth: 760, lineHeight: 1.5 }}>
        The launch checklist for global payouts. <b>Built</b> means we think it works;{" "}
        <b>✓ Verified</b> means the payments expert confirmed it. The MassPay integration flag stays
        off until every launch gate is verified. Full background:{" "}
        <code className="adm-fs-76">docs/payouts-compliance-model.md</code>.
      </p>

      {error && (
        <div className="adm-card adm-mb-12" style={{ padding: 10, borderLeft: "3px solid var(--adm-red)" }}>
          <span className="adm-fs-80">{error}</span>
        </div>
      )}

      {/* Progress cards */}
      <div className="adm-mb-14" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
        <div className="adm-card adm-accent-green">
          <div className="muted-text adm-eyebrow">
            Verified by expert
          </div>
          <div className="adm-big-num">
            <span className="adm-green">{summary.verified}</span>
            <span className="muted-text"> / {summary.total}</span>
          </div>
        </div>
        <div className="adm-card adm-p-14" style={{ borderLeft: "3px solid #f0c000" }}>
          <div className="muted-text adm-eyebrow">
            Built, awaiting sign-off
          </div>
          <div className="adm-big-num">{summary.built}</div>
        </div>
        {summary.liveYes !== undefined && (
          <div className="adm-card adm-accent-green">
            <div className="muted-text adm-eyebrow">
              Live on the app now
            </div>
            <div className="adm-big-num">
              <span className="adm-green">{summary.liveYes}</span>
              <span className="muted-text"> / {summary.total}</span>
            </div>
            <div className="muted-text adm-fs-74 adm-mt-4">
              {summary.livePartial || 0} more partially live — protections actively running in production.
            </div>
          </div>
        )}
        <div className="adm-card" style={{ padding: 14, borderLeft: summary.blocked ? "3px solid var(--adm-red)" : undefined }}>
          <div className="muted-text adm-eyebrow">
            Blocked
          </div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800, color: summary.blocked ? "var(--adm-red)" : undefined }}>
            {summary.blocked}
          </div>
        </div>
        {summary.categories.map((c) => (
          <div key={c.category} className="adm-card adm-p-14">
            <div className="muted-text adm-eyebrow">
              {CATEGORY_ICONS[c.category] || "•"} {c.category}
            </div>
            <div style={{ fontSize: "1.2rem", fontWeight: 800 }}>
              {c.verified}
              <span className="muted-text adm-fs-85"> verified / {c.total}</span>
            </div>
          </div>
        ))}
      </div>

      {Object.entries(byCategory).map(([category, items]) => (
        <div key={category} className="adm-card adm-p-16 adm-mb-14">
          <h3 style={{ margin: "0 0 10px", fontSize: "1rem" }}>
            {CATEGORY_ICONS[category] || "•"} {category}
          </h3>
          <div className="adm-scroll-x">
            <table className="adm-table-light adm-w-full">
              <thead>
                <tr>
                  <th style={{ width: "22%" }}>Guardrail</th>
                  <th style={{ width: "36%" }}>What & why</th>
                  <th>On the app</th>
                  <th>Status</th>
                  <th style={{ width: "22%" }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.key}>
                    <td className="adm-bold">{item.title}</td>
                    <td className="muted-text adm-fs-78" style={{ lineHeight: 1.45 }}>{item.detail}</td>
                    <td className="adm-nowrap" style={{ maxWidth: 170 }}>
                      <LiveBadge live={item.live} />
                      {item.liveNote && (
                        <div className="muted-text" style={{ fontSize: "0.68rem", whiteSpace: "normal", lineHeight: 1.4, marginTop: 3 }}>
                          {item.liveNote}
                        </div>
                      )}
                    </td>
                    <td className="adm-nowrap">
                      <StatusChip status={item.status} />
                      <select
                        className="adm-select adm-block adm-mt-6 adm-fs-74"
                        value={item.status}
                        disabled={savingKey === item.key}
                        onChange={(e) => patch(item.key, { status: e.target.value })}
                      >
                        {data.statuses.map((s) => (
                          <option key={s} value={s}>{STATUS_STYLE[s]?.label || s}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <textarea
                        className="adm-select adm-w-full adm-fs-76"
                        style={{ minHeight: 54, resize: "vertical" }}
                        placeholder="Running notes…"
                        value={noteDrafts[item.key] ?? item.notes}
                        onChange={(e) => setNoteDrafts((d) => ({ ...d, [item.key]: e.target.value }))}
                        onBlur={() => {
                          const draft = noteDrafts[item.key];
                          if (draft !== undefined && draft !== item.notes) patch(item.key, { notes: draft });
                        }}
                      />
                      {item.updatedAt && (
                        <div className="muted-text" style={{ fontSize: "0.68rem", marginTop: 2 }}>
                          updated {new Date(item.updatedAt).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
